import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, HelpCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";

function Block({ block }) {
  if (block.image) {
    return (
      <figure className="mt-3" data-testid="help-image">
        <img
          src={`${API}/help/asset/${block.image}`}
          alt={block.caption || block.image}
          loading="lazy"
          className="w-full rounded-lg border border-slate-200 shadow-sm"
        />
        {block.caption && <figcaption className="mt-1.5 text-xs text-slate-400">{block.caption}</figcaption>}
      </figure>
    );
  }
  if (block.p) return <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="help-paragraph">{block.p}</p>;
  return (
    <div className="mt-2">
      {block.sub && <h3 className="mt-4 text-sm font-700 text-slate-800">{block.sub}</h3>}
      {block.steps && (
        <ol className="mt-1.5 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-slate-600 marker:text-indigo-400 marker:font-semibold">
          {block.steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      )}
      {block.bullets && (
        <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm leading-relaxed text-slate-600 marker:text-indigo-400">
          {block.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function Help() {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/help/content")
      .then(({ data }) => setContent(data))
      .catch(() => toast.error(t("help.loadError")));
  }, [t]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/help/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "BHF-彩卡助手-使用帮助.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("help.downloadError"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-8 py-10" data-testid="help-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-700 text-slate-900">
            <HelpCircle className="h-6 w-6 text-indigo-500" /> {content?.title || t("help.pageTitle")}
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">{t("help.intro")}</p>
          {content?.version && <p className="mt-0.5 text-xs text-slate-400">{content.version}</p>}
        </div>
        <Button className="bhf-btn-primary shrink-0 gap-1.5 border-0 text-white" onClick={downloadPdf} disabled={downloading} data-testid="download-help-pdf-button">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {downloading ? t("help.downloading") : t("help.downloadPdf")}
        </Button>
      </div>

      <div className="mt-8 space-y-6">
        {(content?.sections || []).map((sec, i) => (
          <section key={i} className="bhf-card p-6" data-testid={`help-section-${i}`}>
            <h2 className="font-display text-base font-700 text-indigo-600">{sec.title}</h2>
            {(sec.blocks || []).map((b, j) => <Block key={j} block={b} />)}
          </section>
        ))}
        {!content && <div className="py-16 text-center text-sm text-slate-400"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /></div>}
      </div>
    </div>
  );
}
